import { useCallback, useState } from 'react'
import { useMutation, useQuery } from '@apollo/client/react'
import { toast } from '@buffer-mono/popcorn'
import { ORGANIZATION_ACTIONS } from '../events/orgEvents'
import { QUERY_ACCOUNT } from '../graphql/account'
import { prefixWithProduct } from '../utils/prefixWithProduct'
import { redirectToUrl } from '../utils/urls'
import type { Account } from '../types'
import { GetPlanSelectorData } from '../../components/PlanSelector/hooks/usePlanSelectorQuery'
import { graphql } from '../../gql'
import { SubscriptionUpdateStatus } from '../../gql/graphql'
import { useStripePaymentConfirmation } from '../../hooks/useStripePaymentConfirmation'

const forceRefetchProfilesAfterChannelUnlock = ({
  channelIds,
}: {
  channelIds: string[]
}): void => {
  // When users connect a channel while at their plan limit, they are
  // prompted to pay in order to unlock that channel. Once they do, they
  // are able to use Publish, however, we can't directly refetch their
  // profiles as it's done by dispatching an action that makes an RPC
  // request. This workaround forces a page refresh to refetch their
  // profiles if they have just recently successfully connected a channel
  // and are in Publish.
  const publishEnvUrls = [
    'publish.local.buffer.com:8888/channels',
    'publish.dev.buffer.com/channels',
    'publish.buffer.com/channels',
  ]

  const isVisitingPublish = publishEnvUrls.some((url) =>
    window.location.href.includes(url),
  )

  if (isVisitingPublish) {
    const justConnectedChannelId = channelIds[0]
    redirectToUrl(
      `${window.location.href}?channelId=${justConnectedChannelId}&channelConnectionSuccess=true&tab=queue`,
    )
  }
}

export enum UpdatePhase {
  Idle = 'idle',
  Updating = 'updating',
  Authorizing = 'authorizing',
  Confirming = 'confirming',
}

const IncreaseSubscriptionQuantityByChannelIds = graphql(/* GraphQL */ `
  mutation IncreaseSubscriptionQuantityByChannelIds(
    $input: IncreaseSubscriptionQuantityByChannelIdsInput!
  ) {
    increaseSubscriptionQuantityByChannelIds(input: $input) {
      __typename
      ... on UpdateBillingSubscriptionPlanResponse {
        status
        scheduledUpdate {
          newPlan
          newInterval
          newQuantity
          effectiveDate
        }
        paymentAction {
          clientSecret
          paymentMethodId
        }
      }
      ... on VoidMutationError {
        message
      }
    }
  }
`)

const useUpdateSubscriptionQuantityByChannelIds = ({
  user,
  channelIds,
  cta,
  upgradePathName,
}: {
  user: Account
  channelIds: string[]
  cta: string
  upgradePathName: string
}): {
  updateSubscriptionQuantityByChannelIds: () => Promise<void>
  error: { message: string } | null
  isProcessing: boolean
  updatePhase: UpdatePhase
} => {
  const [updatePhase, setUpdatePhase] = useState(UpdatePhase.Idle)
  const [error, setError] = useState<{
    message: string
  } | null>(null)

  /**
   * Payment retry state (VLG-394)
   *
   * NOTE: This hook uses individual useState instead of useScaRetryState because:
   * - channelIds come from props and don't change during retry flow
   * - No parameter change detection needed (unlike PlanSelector/PlanCardsVertical)
   * - Simpler retry logic: just save/reuse clientSecret
   *
   * If this hook ever needs parameter tracking, refactor to useScaRetryState.
   */
  const [savedClientSecret, setSavedClientSecret] = useState<string | null>(
    null,
  )
  const [savedPaymentMethodId, setSavedPaymentMethodId] = useState<
    string | null
  >(null)
  const [isRetrying, setIsRetrying] = useState(false)

  const {
    confirmPayment,
    isConfirming,
    isFeatureEnabled: isScaEnabled,
  } = useStripePaymentConfirmation()

  // Get refetch function from QUERY_ACCOUNT to manually refetch after SCA confirmation
  const { refetch: refetchAccount } = useQuery(QUERY_ACCOUNT, {
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  })

  // Get refetch function from GetPlanSelectorData to keep plan data in sync
  const { refetch: refetchPlanSelector } = useQuery(GetPlanSelectorData, {
    variables: {
      input: {
        organizationId: user.currentOrganization?.id ?? '',
      },
    },
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  })

  const [mutate, { loading: mutationLoading }] = useMutation(
    IncreaseSubscriptionQuantityByChannelIds,
    {
      refetchQueries: [
        { query: QUERY_ACCOUNT },
        {
          query: GetPlanSelectorData,
          variables: {
            input: {
              organizationId: user.currentOrganization?.id ?? '',
            },
          },
        },
      ],
      awaitRefetchQueries: true,
    },
  )

  const isProcessing = mutationLoading || isConfirming

  const updateSubscriptionQuantityByChannelIds =
    useCallback(async (): Promise<void> => {
      // Validate required parameters
      if (!user.currentOrganization?.id) {
        const errorMessage = 'Organization ID is required'
        setError({ message: errorMessage })
        toast.error(errorMessage)
        return
      }

      if (!channelIds || channelIds.length === 0) {
        const errorMessage = 'No channels selected for upgrade'
        setError({ message: errorMessage })
        toast.error(errorMessage)
        return
      }

      setError(null)

      // SCENARIO A: Pure retry - reuse client secret (VLG-394)
      if (isScaEnabled && savedClientSecret && isRetrying) {
        setUpdatePhase(UpdatePhase.Authorizing)

        let confirmResult
        try {
          confirmResult = await confirmPayment(
            savedClientSecret,
            savedPaymentMethodId ?? undefined,
          )
        } catch {
          setUpdatePhase(UpdatePhase.Idle)
          const errorMessage = 'Payment authorization failed. Please try again.'
          setError({ message: errorMessage })
          toast.error(errorMessage)
          return
        }

        if (confirmResult.success) {
          // Success - clear all saved state
          setSavedClientSecret(null)
          setSavedPaymentMethodId(null)
          setIsRetrying(false)

          // Payment confirmed - wait for backend to process webhook and unlock channels
          setUpdatePhase(UpdatePhase.Confirming)

          try {
            // Wait for backend confirmation that subscription is updated and channels are unlocked
            await Promise.all([refetchAccount(), refetchPlanSelector()])

            // Only after refetch succeeds, trigger side effects
            ORGANIZATION_ACTIONS.billingUpdated({ account: user })
            forceRefetchProfilesAfterChannelUnlock({ channelIds })

            // Reset and show success
            setUpdatePhase(UpdatePhase.Idle)
            toast.success('Channels unlocked successfully')
            return
          } catch {
            // Handle refetch failure
            setUpdatePhase(UpdatePhase.Idle)
            const errorMessage =
              'Payment confirmed but unable to verify channel unlock. Please refresh the page.'
            setError({ message: errorMessage })
            toast.error(errorMessage)
            return
          }
        } else {
          // Still failed - keep retry mode active
          setUpdatePhase(UpdatePhase.Idle)
          const errorMessage = 'Payment authorization failed. Please try again.'
          setError({ message: errorMessage })
          toast.error(errorMessage)
          return
        }
      }

      // SCENARIO B: New attempt (VLG-394)
      // Clear saved state
      setIsRetrying(false)
      setSavedClientSecret(null)
      setSavedPaymentMethodId(null)

      setUpdatePhase(UpdatePhase.Updating)

      try {
        // Call mutation with wrapped input
        const result = await mutate({
          variables: {
            input: {
              organizationId: user.currentOrganization?.id ?? '',
              channelIds,
              attribution: {
                cta: prefixWithProduct(cta),
                upgradePathName,
              },
            },
          },
        })

        const response = result.data?.increaseSubscriptionQuantityByChannelIds

        // Handle errors
        if (response?.__typename === 'VoidMutationError') {
          const errorMessage = response.message || 'Unknown error occurred'
          setError({ message: errorMessage })
          setUpdatePhase(UpdatePhase.Idle)
          toast.error(errorMessage)
          return
        }

        if (response?.__typename !== 'UpdateBillingSubscriptionPlanResponse') {
          const errorMessage = 'Unexpected response from server'
          setError({ message: errorMessage })
          setUpdatePhase(UpdatePhase.Idle)
          toast.error(errorMessage)
          return
        }

        // Check if SCA required
        if (
          isScaEnabled &&
          response.status === SubscriptionUpdateStatus.RequiresAction &&
          response.paymentAction?.clientSecret
        ) {
          // Save client secret and payment method for potential retry (VLG-394)
          setSavedClientSecret(response.paymentAction.clientSecret)
          setSavedPaymentMethodId(
            response.paymentAction.paymentMethodId ?? null,
          )

          // Start authorization phase
          setUpdatePhase(UpdatePhase.Authorizing)

          // Trigger Stripe 3D Secure (Stripe displays modal automatically)
          let confirmResult
          try {
            confirmResult = await confirmPayment(
              response.paymentAction.clientSecret,
              response.paymentAction.paymentMethodId ?? undefined,
            )
          } catch {
            setUpdatePhase(UpdatePhase.Idle)
            // Enable retry mode (VLG-394)
            setIsRetrying(true)
            const errorMessage =
              'Payment authorization failed. Please try again.'
            setError({ message: errorMessage })
            toast.error(errorMessage)
            return
          }

          // Handle authorization result
          if (confirmResult.success) {
            // Payment confirmed - clear all saved state (VLG-394)
            setSavedClientSecret(null)
            setSavedPaymentMethodId(null)
            setIsRetrying(false)

            // Wait for backend to process webhook and unlock channels
            setUpdatePhase(UpdatePhase.Confirming)

            try {
              // Wait for backend confirmation that subscription is updated and channels are unlocked
              await Promise.all([refetchAccount(), refetchPlanSelector()])

              // Only after refetch succeeds, trigger side effects
              ORGANIZATION_ACTIONS.billingUpdated({ account: user })
              forceRefetchProfilesAfterChannelUnlock({ channelIds })

              // Reset and show success
              setUpdatePhase(UpdatePhase.Idle)
              toast.success('Channels unlocked successfully')
              return
            } catch {
              // Handle refetch failure
              setUpdatePhase(UpdatePhase.Idle)
              const errorMessage =
                'Payment confirmed but unable to verify channel unlock. Please refresh the page.'
              setError({ message: errorMessage })
              toast.error(errorMessage)
              return
            }
          } else {
            // User canceled or payment failed - enable retry mode (VLG-394)
            setIsRetrying(true)
            setUpdatePhase(UpdatePhase.Idle)
            const errorMessage =
              'Payment authorization failed. Please try again.'
            setError({ message: errorMessage })
            toast.error(errorMessage)
            return
          }
        }

        // Normal success flow (no SCA required)
        if (response.status === SubscriptionUpdateStatus.Success) {
          // Trigger side effects
          ORGANIZATION_ACTIONS.billingUpdated({ account: user })
          forceRefetchProfilesAfterChannelUnlock({ channelIds })

          setUpdatePhase(UpdatePhase.Idle)
          toast.success('Channels unlocked successfully')
          return
        }

        // Unexpected status
        const errorMessage = 'Unexpected response status'
        setError({ message: errorMessage })
        setUpdatePhase(UpdatePhase.Idle)
        toast.error(errorMessage)
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : 'There has been an issue. Please try again.'
        setError({ message: errorMessage })
        setUpdatePhase(UpdatePhase.Idle)
        toast.error(errorMessage)
      }
    }, [
      user,
      channelIds,
      cta,
      upgradePathName,
      mutate,
      isScaEnabled,
      confirmPayment,
      refetchAccount,
      refetchPlanSelector,
      savedClientSecret,
      savedPaymentMethodId,
      isRetrying,
    ])

  return {
    updateSubscriptionQuantityByChannelIds,
    error,
    isProcessing,
    updatePhase,
  }
}

export default useUpdateSubscriptionQuantityByChannelIds
